import apiClient, { ApiResponse, PaginatedResponse } from './client';

// ═══════════════════════════════════════════════════════════════════
// TYPES (mirror apps/api designs/dto/review.dto.ts)
// ═══════════════════════════════════════════════════════════════════

export type ReviewState = 'NEEDS_REVIEW' | 'APPROVED' | 'PUBLISH_FAILED';

export interface ReviewQueueMockup {
  imageUrl: string | null;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | string;
  positionSlug: string;
}

export interface ReviewQueueProduct {
  productId: string;
  name: string;
}

export interface ReviewQueueItem {
  id: string;
  name: string;
  thumbnailUrl: string | null;
  reviewState: ReviewState;
  publishFailReason: string | null;
  createdVia: string | null;
  createdAt: string;
  updatedAt: string;
  products: ReviewQueueProduct[];
  mockups: ReviewQueueMockup[];
  mockupSummary: {
    total: number;
    completed: number;
    pending: number;
    failed: number;
  };
}

export interface ReviewActionItem {
  designId: string;
  ok: boolean;
  error?: { code: string; message: string };
}

/**
 * Unified action response: single approvals/retries run synchronously
 * (jobId null, one item); batches enqueue a publish job (jobId set,
 * items carry only immediate per-item conflicts).
 */
export interface ReviewActionResponse {
  jobId: string | null;
  items: ReviewActionItem[];
}

export interface ReviewJobReportItem {
  index: number;
  ok: boolean;
  designId?: string;
  warnings?: string[];
  notes?: string[];
  error?: { code: string; message: string };
}

export interface ReviewJobStatus {
  job: {
    id: string;
    type: string;
    status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
    createdAt: string;
    startedAt: string | null;
    finishedAt: string | null;
    summary: { total: number; succeeded: number; failed: number } | null;
    jobError: { code: string; message: string } | null;
    itemCount: number;
  };
  items: ReviewJobReportItem[];
  nextCursor: string | null;
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Synchronous single-design publish can wait for mockup renders —
 * give the approve/retry calls a generous timeout (the shared client
 * default is 30s).
 */
const PUBLISH_TIMEOUT_MS = 120_000;

export async function getReviewQueue(
  page = 1,
  limit = 50
): Promise<PaginatedResponse<ReviewQueueItem>> {
  const response = await apiClient.get<
    ApiResponse<PaginatedResponse<ReviewQueueItem>>
  >(`/designs/review/queue?page=${page}&limit=${limit}`);
  return response.data.data;
}

export async function approveReviewDesigns(
  designIds: string[]
): Promise<ReviewActionResponse> {
  const response = await apiClient.post<ApiResponse<ReviewActionResponse>>(
    '/designs/review/approve',
    { designIds },
    { timeout: PUBLISH_TIMEOUT_MS }
  );
  return response.data.data;
}

export async function discardReviewDesigns(
  designIds: string[]
): Promise<ReviewActionResponse> {
  const response = await apiClient.post<ApiResponse<ReviewActionResponse>>(
    '/designs/review/discard',
    { designIds }
  );
  return response.data.data;
}

export async function retryReviewDesigns(
  designIds: string[]
): Promise<ReviewActionResponse> {
  const response = await apiClient.post<ApiResponse<ReviewActionResponse>>(
    '/designs/review/retry',
    { designIds },
    { timeout: PUBLISH_TIMEOUT_MS }
  );
  return response.data.data;
}

export async function getReviewJob(
  jobId: string,
  cursor?: string,
  limit?: number
): Promise<ReviewJobStatus> {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', limit.toString());
  const query = params.toString();
  const response = await apiClient.get<ApiResponse<ReviewJobStatus>>(
    `/designs/review/jobs/${jobId}${query ? `?${query}` : ''}`
  );
  return response.data.data;
}
